import { useCallback, useEffect, useState } from 'react';
import { PaymentError, PaymentFormType, PaymentProcessSteps } from '../entities';
import { usePayment } from './usePayment';

type FailedSnapshot = {
  error: PaymentError | null;
};

type UsePaymentFailedProps = {
  offerId: string;
  paymentFormType: PaymentFormType;
  onRetry?: () => void;
};

export const usePaymentFailed = ({ offerId, paymentFormType, onRetry }: UsePaymentFailedProps) => {
  const { paymentProcessStep, paymentError, clearPaymentState, initializePaymentFormRequest } =
    usePayment({ offerId, paymentFormType });
  const [failedSnapshot, setFailedSnapshot] = useState<FailedSnapshot | null>(null);

  const isSubscriptionFlow = paymentFormType === PaymentFormType.SUBSCRIPTION;

  useEffect(() => {
    if (!isSubscriptionFlow || paymentProcessStep !== PaymentProcessSteps.FAILED) return;

    // Snapshot the error: the retry UI re-initialises the form, which resets the store step
    setFailedSnapshot({ error: paymentError });
    clearPaymentState();
  }, [isSubscriptionFlow, paymentProcessStep, paymentError, clearPaymentState]);

  // No close command on purpose: closing the dialog keeps the snapshot (as the legacy form did),
  // so reopening shows the same error with "Try again"
  const clearFailed = useCallback(() => setFailedSnapshot(null), []);

  const retryPayment = useCallback(() => {
    if (onRetry) {
      onRetry();
    }

    clearFailed();
    initializePaymentFormRequest();
  }, [onRetry, clearFailed, initializePaymentFormRequest]);

  return {
    isFailed: !!failedSnapshot,
    failedError: failedSnapshot?.error ?? null,
    clearFailed,
    retryPayment,
  };
};
